import moment from 'moment';
import reverse from 'lodash/reverse';
import cloneDeep from 'lodash/cloneDeep';
import { periodTypesEnum, thresholdLogicEnum, dateRangeNameEnum, localeCodeEnum } from './enums';
import { orgUnitLevels, relativeDateRanges, leadingYearDateFormat } from './constants';

export const getLowestVisibleLevel = () => {
	const levelsObject = {};
	const minimum = window.VisibleOrgUnits.reduce((lowestVisibleLevel, currentOrgUnit) => {
		let unitLevel = levelsObject[currentOrgUnit.orgUnitLevelName];

		if (!unitLevel) {
			unitLevel = orgUnitLevels.find(x => x.name === currentOrgUnit.orgUnitLevelName);

			if (unitLevel) {
				levelsObject[currentOrgUnit.orgUnitLevelName] = unitLevel;
			}
		}

		return !lowestVisibleLevel || unitLevel.id < lowestVisibleLevel.id ? unitLevel : lowestVisibleLevel;
	}, null);

	return minimum;
};

export const dateToString = date => {
	const momentData = moment(date);

	return momentData.format(leadingYearDateFormat);
};

export const yesterday = () => {
	const yd = moment().subtract(1, 'days');

	return dateToString(yd.toDate());
};

export const isDateRangeValid = (dateFrom, dateTo) => moment(dateFrom).startOf('day') <= moment(dateTo).startOf('day');

export const setupInitialOrgUnitSelection = (selectAllOnLowestLevel = false) => {
	let lowestVisibleLevel;
	let lowestVisibleLevelOrgUnitsInAlphabeticalOrder;

	if (window.VisibleOrgUnits) {
		lowestVisibleLevel = getLowestVisibleLevel();
		lowestVisibleLevelOrgUnitsInAlphabeticalOrder = window.VisibleOrgUnits.filter(
			x => x.orgUnitLevelName === lowestVisibleLevel.name
		).sort((x, y) => {
			const xname = x.name.toUpperCase();
			const yname = y.name.toUpperCase();

			if (xname < yname) {
				return -1;
			}

			if (xname > yname) {
				return 1;
			}

			return 0;
		});
	}

	const selectedOrgUnits = orgUnitLevels.map(x => {
		const selectedOrgUnitsObject = { level: x };
		const selectedArray =
			selectAllOnLowestLevel && lowestVisibleLevel.name === orgUnitLevels[4].name
				? lowestVisibleLevelOrgUnitsInAlphabeticalOrder
				: [lowestVisibleLevelOrgUnitsInAlphabeticalOrder[0]];
		selectedOrgUnitsObject.orgUnits = lowestVisibleLevel && x.name === lowestVisibleLevel.name ? selectedArray : [];

		return selectedOrgUnitsObject;
	});

	return selectedOrgUnits;
};

export const calculateDatePeriods = (dateFrom, dateTo, periodType) => {
	const periods = [];
	const dateFromDate = new Date(dateFrom);
	const dateToDate = new Date(dateTo);
	dateFromDate.setHours(0, 0, 0, 0);
	dateToDate.setHours(0, 0, 0, 0);

	let startDate;

	if (periodType === periodTypesEnum.Daily) {
		for (startDate = dateFromDate; startDate <= dateToDate; startDate.setDate(startDate.getDate() + 1)) {
			periods.push({ dateFrom: dateToString(new Date(startDate.getTime())), dateTo: null });
		}
	} else if (periodType === periodTypesEnum.Weekly) {
		for (startDate = dateFromDate; startDate <= dateToDate; startDate.setDate(startDate.getDate() + 7)) {
			const dPlusSevenDaysDate = new Date(startDate.getTime());
			dPlusSevenDaysDate.setDate(dPlusSevenDaysDate.getDate() + 6);
			periods.push({
				dateFrom: dateToString(new Date(startDate.getTime())),
				dateTo: dateToString(dPlusSevenDaysDate)
			});
		}
	} else if (periodType === periodTypesEnum.FourWeeks) {
		for (startDate = dateFromDate; startDate <= dateToDate; startDate.setDate(startDate.getDate() + 28)) {
			const dPlus27DaysDate = new Date(startDate.getTime());
			dPlus27DaysDate.setDate(dPlus27DaysDate.getDate() + 27);
			periods.push({
				dateFrom: dateToString(new Date(startDate.getTime())),
				dateTo: dateToString(dPlus27DaysDate)
			});
		}
	} else if (periodType === periodTypesEnum.Monthly) {
		for (startDate = dateFromDate; startDate <= dateToDate; startDate.setMonth(startDate.getMonth() + 1)) {
			const dPlusOneMonth = new Date(startDate.getTime());
			dPlusOneMonth.setMonth(startDate.getMonth() + 1);
			const monthDifference =
				dPlusOneMonth.getMonth() -
				startDate.getMonth() +
				12 * (dPlusOneMonth.getFullYear() - startDate.getFullYear());

			if (monthDifference === 2) {
				startDate.setMonth(startDate.getMonth() + 1);
				startDate.setDate(1);
				startDate.setMonth(startDate.getMonth() - 1);
				dPlusOneMonth.setDate(0);
			} else {
				dPlusOneMonth.setDate(dPlusOneMonth.getDate() - 1);
			}

			periods.push({
				dateFrom: dateToString(new Date(startDate.getTime())),
				dateTo: dateToString(dPlusOneMonth)
			});
		}
	} else if (periodType === periodTypesEnum.Quarterly) {
		for (
			startDate = moment(dateFromDate);
			startDate.isSameOrBefore(moment(dateToDate), 'quarter');
			startDate.add(1, 'quarters').startOf('quarter')
		) {
			periods.push({
				dateFrom: dateToString(startDate),
				dateTo: dateToString(moment(startDate).endOf('quarter'))
			});
		}
	} else if (periodType === periodTypesEnum.WholePeriod) {
		periods.push({ dateFrom: dateToString(dateFromDate), dateTo: dateToString(dateToDate) });
	}

	return reverse(periods);
};

export const datesAreOnSameDay = (first, second) =>
	first.getFullYear() === second.getFullYear() &&
	first.getMonth() === second.getMonth() &&
	first.getDate() === second.getDate();

export const determineThresholdLogic = settings => {
	if (
		settings === undefined ||
		settings.aboveRed === undefined ||
		settings.aboveYellow === undefined ||
		settings.belowRed === undefined ||
		settings.belowYellow === undefined
	) {
		return thresholdLogicEnum.Error;
	}

	if (
		[settings.aboveRed, settings.aboveYellow, settings.belowRed, settings.belowYellow].every(
			x => x === settings.aboveRed
		)
	) {
		return thresholdLogicEnum.SingleValue;
	}

	if (
		(settings.aboveRed !== 0 || settings.aboveYellow !== 0) &&
		(settings.belowRed !== 0 || settings.belowYellow !== 0)
	) {
		return thresholdLogicEnum.Default;
	}

	if (settings.aboveRed === 0 && settings.aboveYellow === 0) {
		return thresholdLogicEnum.UseLowerValues;
	}

	if (settings.belowRed === 0 && settings.belowYellow === 0) {
		return thresholdLogicEnum.UseHigherValues;
	}

	return thresholdLogicEnum.Error;
};

export const getBrowser = () => {
	const ua = window.navigator.userAgent;
	let version = false;

	// Test values; Uncomment to check result …

	// IE 10
	// ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';

	// IE 11
	// ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';

	// Edge 12 (Spartan)
	// ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';

	// Edge 13
	// ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

	const msie = ua.indexOf('MSIE ');
	if (msie > 0) {
		// IE 10 or older => return version number
		version = true;
	}

	const trident = ua.indexOf('Trident/');
	if (trident > 0) {
		version = true;
	}

	const edge = ua.indexOf('Edge/');
	if (edge > 0) {
		version = true;
	}

	// other browser
	return version;
};

export const getColorModes = () => {
	const colors = [
		{
			Id: 1,
			Name: 'label_ColorLight'
		},
		{
			Id: 2,
			Name: 'label_ColorDark'
		}
	];

	if (!window.isIEorEdgeHTML) {
		colors.unshift({
			Id: 0,
			Name: 'label_SystemDefault'
		});
	}

	return colors;
};

export const getColorPalettes = () => {
	const colorPalettes1 = [
		{
			Id: 0,
			Name: 'label_ColorBlue',
			Colors: {
				Primary: '#4339E5',
				Secondary: '#5B4FED',
				Tertiary: '#EAE9F9',
				PanelHeader: '#F0EFFE'
			}
		},
		{
			Id: 1,
			Name: 'label_ColorMint',
			Colors: {
				Primary: '#386f87',
				Secondary: '#5d8ba0',
				Tertiary: '#dee5ec',
				PanelHeader: '#dee5ec'
			}
		}
	];

	const colorPalettes = [
		{
			Id: 0,
			Name: 'label_ColorBlue',
			Colors: {
				Primary: '#4339E5',
				Secondary: '#5B4FED',
				Tertiary: '#EAE9F9',
				PanelHeader: '#F0EFFE'
			}
		}
	];
	const currentTime = new Date();

	const christmasRangeBegin = new Date(new Date().getFullYear(), 11, 1);
	const christmasRangeEnd = new Date(new Date().getFullYear() + 1, 0, 15);

	if (currentTime >= christmasRangeBegin && currentTime <= christmasRangeEnd) {
		colorPalettes.push({
			Id: colorPalettes.length,
			Name: 'Jingle Jingle (Limited Edition)',
			Colors: {
				Primary: '#344d2f',
				Secondary: '#a81817',
				Tertiary: '#f0f4f7',
				PanelHeader: '#f0f4f7'
			}
		});
	}

	const prideMonthBegin = new Date(new Date().getFullYear(), 4, 25);
	const prideMonthEnd = new Date(new Date().getFullYear(), 5, 30);

	if (currentTime >= prideMonthBegin && currentTime <= prideMonthEnd) {
		colorPalettes.push({
			Id: colorPalettes.length,
			Name: 'label_ColorPride',
			Colors: {
				Primary: '#4339E5',
				Secondary: '#5B4FED',
				Tertiary: '#f0f4f7',
				PanelHeader: '#EAE9F9',
				PrimaryGradient:
					'linear-gradient(90deg, #80E8DD 0%, #f7CC2F 20%, #AF81E4 40%, #E784BA 60%, #F9C1A0 80%, #A0C4FF 100%)'
			}
		});
	}

	return colorPalettes;
};

export const calculateRelativeDateRange = dateRange => {
	const today = moment().locale(localeCodeEnum.EnglishUnitedKingdom).startOf('day');

	switch (dateRange) {
		case dateRangeNameEnum.Yesterday:
			const previousDay = moment(today).subtract(1, 'days');

			return [previousDay, previousDay];

		case dateRangeNameEnum.Today:
			return [today, today];

		case dateRangeNameEnum.Tomorrow:
			const tomorrow = moment(today).add(1, 'days');

			return [tomorrow, tomorrow];

		case dateRangeNameEnum.LastWeek:
			const lastWeek = moment(today).subtract(1, 'weeks');

			return [moment(lastWeek).startOf('week'), moment(lastWeek).endOf('week')];

		case dateRangeNameEnum.CurrentWeek:
			return [moment(today.startOf('week')), moment(today).endOf('week')];

		case dateRangeNameEnum.NextWeek:
			const nextWeek = moment(today).add(1, 'weeks');

			return [moment(nextWeek.startOf('week')), moment(nextWeek).endOf('week')];

		case dateRangeNameEnum.LastMonth:
			const lastMonth = moment(today).subtract(1, 'months');

			return [moment(lastMonth.startOf('month')), moment(lastMonth).endOf('month')];

		case dateRangeNameEnum.CurrentMonth:
			return [moment(today.startOf('month')), moment(today).endOf('month')];

		case dateRangeNameEnum.NextMonth:
			const nextMonth = moment(today).add(1, 'months');

			return [moment(nextMonth.startOf('month')), moment(nextMonth).endOf('month')];

		case dateRangeNameEnum.LastQuarter:
			const lastQuarter = moment(today).subtract(1, 'quarters');

			return [moment(lastQuarter.startOf('quarter')), moment(lastQuarter).endOf('quarter')];

		case dateRangeNameEnum.CurrentQuarter:
			return [moment(today.startOf('quarter')), moment(today).endOf('quarter')];

		case dateRangeNameEnum.NextQuarter:
			const nextQuarter = moment(today).add(1, 'quarters');

			return [moment(nextQuarter.startOf('quarter')), moment(nextQuarter).endOf('quarter')];

		case dateRangeNameEnum.LastYear:
			const lastYear = moment(today).subtract(1, 'years');

			return [moment(lastYear.startOf('year')), moment(lastYear).endOf('year')];

		case dateRangeNameEnum.CurrentYear:
			return [moment(today.startOf('year')), moment(today).endOf('year')];

		case dateRangeNameEnum.NextYear:
			const nextYear = moment(today).add(1, 'years');

			return [moment(nextYear.startOf('year')), moment(nextYear).endOf('year')];

		case dateRangeNameEnum.LastRosterCycle:
			const lastRosterCycleFrom = window.lastRosterCycleDateFrom;
			const lastRosterCycleTo = window.lastRosterCycleDateTo;

			return [moment(lastRosterCycleFrom), moment(lastRosterCycleTo)];

		case dateRangeNameEnum.CurrentRosterCycle:
			const currentRosterCycleFrom = window.currentRosterCycleDateFrom;
			const currentRosterCycleTo = window.currentRosterCycleDateTo;

			return [moment(currentRosterCycleFrom), moment(currentRosterCycleTo)];

		case dateRangeNameEnum.NextRosterCycle:
			const nextRosterCycleFrom = window.nextRosterCycleDateFrom;
			const nextRosterCycleTo = window.nextRosterCycleDateTo;

			return [moment(nextRosterCycleFrom), moment(nextRosterCycleTo)];

		default:
			return [today, today];
	}
};

export const localizeDateRange = (dateRange, dateFormat, parseFormat) => {
	if (dateRange === null || dateRange === undefined) {
		return '';
	}

	const dates = dateRange.split(' - ');

	return `${moment(dates[0], parseFormat).format(dateFormat)} - ${moment(dates[1], parseFormat).format(dateFormat)}`;
};

export const findDateRangeLabelKey = dateRangeName => {
	const correspondingDateRange = relativeDateRanges.find(dateRange => dateRange.name === dateRangeName);

	if (correspondingDateRange !== undefined) {
		return correspondingDateRange.labelKey;
	}

	return '';
};

export const getLocaleCodeForCompareFunction = locale => {
	switch (locale) {
		case localeCodeEnum.Danish:
			return localeCodeEnum.Danish;

		case localeCodeEnum.German:
			return localeCodeEnum.German;

		default:
			return 'en';
	}
};

export const createMetricOrder = metrics => {
	const transformedOrderedMetrics = [];
	let loopNumber = 0;

	if (metrics.length > 0) {
		metrics.forEach(metricGroup => {
			metricGroup.metrics.forEach(metric => {
				transformedOrderedMetrics.push({
					metricGroup: metricGroup.fieldGroupLabel,
					metricName: metric.metricName,
					position: loopNumber
				});

				loopNumber += 1;
			});
		});

		loopNumber = 0;
	}

	return transformedOrderedMetrics;
};

export const hash = (str, seed = 0) => {
	let h1 = 0xdeadbeef ^ seed;
	let h2 = 0x41c6ce57 ^ seed;

	for (let i = 0, ch; i < str.length; i++) {
		ch = str.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}

	h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

	return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

export const parseChartData = data => {
	if (data !== null) {
		for (let i = 0; i < data.length; i++) {
			if (data[i].value !== null) {
				data[i].value = parseFloat(data[i].value.replace(',', ''));
			}
		}
	}

	return data;
};

export const calculateNumberOfDaysInMonth = referenceDate => {
	const dateFrom = new Date(referenceDate);
	const dateFromToString = dateToString(dateFrom);
	const dPlusOneMonth = new Date(dateFromToString);
	dPlusOneMonth.setMonth(dateFrom.getMonth() + 1);
	const difference =
		dPlusOneMonth.getMonth() - dateFrom.getMonth() + 12 * (dPlusOneMonth.getFullYear() - dateFrom.getFullYear());

	if (difference === 2) {
		dateFrom.setMonth(dateFrom.getMonth() + 1);
		dateFrom.setDate(1);
		dateFrom.setMonth(dateFrom.getMonth() - 1);
		dPlusOneMonth.setDate(0);
	} else {
		dPlusOneMonth.setDate(dPlusOneMonth.getDate() - 1);
	}

	const days = Math.round((dPlusOneMonth - dateFrom) / (1000 * 60 * 60 * 24)) + 1;

	return days;
};

export const calculateNumberOfDaysInQuarter = referenceDate => {
	const startOfQuarter = moment(referenceDate).startOf('quarter');
	const endOfQuarter = moment(referenceDate).endOf('quarter');
	const lengthOfQuarter = endOfQuarter.diff(startOfQuarter, 'days') + 1;

	return lengthOfQuarter;
};

export const getPeriodType = (currentPeriodType, dateFrom, dateTo) => {
	const monthlyPeriodInDays = calculateNumberOfDaysInMonth(dateFrom);
	const quarterInDays = calculateNumberOfDaysInQuarter(dateFrom);
	const daysDifference = Math.round((dateTo - dateFrom) / (1000 * 60 * 60 * 24)) + 1;
	let newPeriodType = null;

	if (
		(currentPeriodType === periodTypesEnum.Weekly && daysDifference < 7) ||
		(currentPeriodType === periodTypesEnum.FourWeeks && daysDifference < 28) ||
		(currentPeriodType === periodTypesEnum.Monthly && daysDifference < monthlyPeriodInDays) ||
		(currentPeriodType === periodTypesEnum.Quarterly && daysDifference < quarterInDays)
	) {
		newPeriodType = periodTypesEnum.WholePeriod;
	} else {
		newPeriodType = currentPeriodType;
	}

	return newPeriodType;
};

export const findLowestFilledLevel = orgUnitsSelection => {
	const selectionItemsWithOrgUnits = orgUnitsSelection.filter(x => x.orgUnits.length > 0);

	return selectionItemsWithOrgUnits[selectionItemsWithOrgUnits.length - 1].level.id;
};

export const getWardLevelUnits = model => {
	let orgUnitsList = [];
	let orgUnitsSelectionLevel = null;
	const selectedOrgUnitsLength = model.selectedOrgUnits.length;
	for (let i = 0; i < selectedOrgUnitsLength; i++) {
		const selectedOrgUnitsOnLevel = model.selectedOrgUnits[selectedOrgUnitsLength - i - 1];

		if (selectedOrgUnitsOnLevel.orgUnits.length > 0) {
			orgUnitsList = selectedOrgUnitsOnLevel.orgUnits;
			orgUnitsSelectionLevel = selectedOrgUnitsOnLevel.level;
			break;
		}
	}

	const dateFromSelected =
		model.selectedDatePeriod !== undefined && model.selectedDatePeriod !== null
			? model.selectedDatePeriod.datePeriod.dateFrom
			: model.dateFromSelected;
	const dateToSelected =
		model.selectedDatePeriod !== undefined && model.selectedDatePeriod !== null
			? model.selectedDatePeriod.datePeriod.dateTo
				? model.selectedDatePeriod.datePeriod.dateTo
				: model.selectedDatePeriod.datePeriod.dateFrom
			: model.dateToSelected;
	let url = '';
	let requestParams;

	if (orgUnitsSelectionLevel !== null && orgUnitsSelectionLevel.id === 5) {
		url = 'Unit/GetOrgUnitsByDatePeriod';
		requestParams = {
			orgunitId: orgUnitsList.map(x => x.orgUnitID),
			dateFrom: dateFromSelected,
			dateTo: dateToSelected
		};
	} else if (orgUnitsSelectionLevel !== null && orgUnitsSelectionLevel.id !== 5) {
		url = 'Unit/GetOrgUnitChildren';
		requestParams = {
			levelName: orgUnitLevels[4].name,
			orgunitId: orgUnitsList[0].orgUnitID,
			dateFrom: dateFromSelected,
			dateTo: dateToSelected
		};
	}

	return { url, requestParams };
};

export const updateReduxStateWithViewRelativeDates = viewFilters => {
	const result = {};

	if (viewFilters.selectedRelativeDateRange !== null && viewFilters.selectedRelativeDateRange !== undefined) {
		const [dateFromValue, dateToValue] = calculateRelativeDateRange(viewFilters.selectedRelativeDateRange);

		const propDateFrom = new Date(viewFilters.dateFrom);
		const propDateTo = new Date(viewFilters.dateTo);

		if (
			datesAreOnSameDay(new Date(dateFromValue), propDateFrom) &&
			datesAreOnSameDay(new Date(dateToValue), propDateTo)
		) {
			result.dateFrom = viewFilters.dateFrom;
			result.dateTo = viewFilters.dateTo;
			result.datePeriods = viewFilters.datePeriods;
			result.selectedDatePeriod = viewFilters.selectedDatePeriod;
		} else {
			result.dateFrom = dateFromValue;
			result.dateTo = dateToValue;
			const checkedDatePeriods = calculateDatePeriods(dateFromValue, dateToValue, viewFilters.selectedPeriodType);
			result.selectedDatePeriod = {
				index: viewFilters.selectedDatePeriod.index,
				datePeriod: checkedDatePeriods[viewFilters.selectedDatePeriod.index]
			};
			result.datePeriods = checkedDatePeriods;
		}
	} else {
		result.dateFrom = viewFilters.dateFrom;
		result.dateTo = viewFilters.dateTo;
		result.datePeriods = viewFilters.datePeriods;
		result.selectedDatePeriod = viewFilters.selectedDatePeriod;
	}

	return result;
};

export const capitalizeFirstLetter = stringValue => stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
